import { OperatorPricingCard } from "./sections/OperatorPricingCard";
import { buildOperatorPricing, resolveOperatorCurrencyCode } from "../operatorPricing";
import { Card } from "@/src/components/wrappers/Card";
import {
  Booking,
  OperatorDetails,
  Operator as OperatorModel,
  OperatorPricing,
  OperatorTransferDetails,
} from "../model";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/src/components/ui/button";
import { parseISO, format } from "date-fns";
import { Separator } from "@/src/components/ui/separator";
import { BookingCard } from "@/src/components/wrappers/BookingCard";
import { CopyIcon, Plus, ExternalLink } from "lucide-react";
import { useBookingData } from "@/src/hooks/useBookingData";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../../components/ui/dialog";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, Form } from "../../../components/ui/form";
import { Input } from "../../../components/ui/input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Textarea } from "../../../components/ui/textarea";
import { ScoreSelect } from "../../../components/ui/score-select";
import { useBookingOperatorMutation } from "../../../hooks/useBookingOperatorMutation";
import { CurrencySelect } from "../../../components/CurrencySelect";
import { useOffersStore } from "../../../stores/offersStore";
import { UploadInvoiceButton } from "./UploadInvoiceButton";
import { paymentsFetch } from "@/src/lib/paymentsFetch";
import { useQuery } from "@tanstack/react-query";

export const Operator = ({
  booking,
  handleCopyToClipboard,
}: {
  booking: Booking;
  handleCopyToClipboard: (text: string) => void;
}) => {
  const { currencies } = useOffersStore();
  const { operatorData, operatorInvoiceFile } = useBookingData(booking);

  const operatorCurrencyCode = operatorData?.transferDetails?.currencyCode || "EUR";
  const operatorCurrency = currencies?.find((c) => c.code === operatorCurrencyCode);
  const pounds = currencies?.find((c) => c.code === "GBP");
  const operatorDetails = !!booking?.operatorDetails
    ? booking.operatorDetails
    : {
        address: operatorData?.address,
        contactPerson: operatorData?.contactPerson,
        email: operatorData?.email,
        email2: operatorData?.email2,
        name: operatorData?.name,
        phoneNumber: operatorData?.phone,
        rating: operatorData?.rating,
        website: operatorData?.website,
      };
  const pricing = booking?.pricing;
  // One calculation for commission and extras on every operator card.
  const operatorPricingView = buildOperatorPricing(booking, currencies, resolveOperatorCurrencyCode(booking, operatorData));

  const operatorTransfer: OperatorTransferDetails = !!booking?.operatorTransfer
    ? booking.operatorTransfer
    : {
        currencyCode: operatorData?.transferDetails?.currencyCode,
        accountNumber: operatorData?.transferDetails?.accountNumber,
        iban: "",
        recipientName: operatorData?.transferDetails?.recipientName,
      };
  return (
    <React.Fragment>
      {operatorData && <OperatorSection operatorDetails={operatorDetails} bookingId={booking.id} />}
      {operatorData && (
        <ReferenceNumberSection
          operator={operatorData}
          initialInvoiceFile={operatorInvoiceFile}
          handleCopyToClipboard={handleCopyToClipboard}
          bookingId={booking.id}
        />
      )}
      {operatorData && <YachtSection booking={booking} operator={operatorData} />}
      {operatorData && (
        <PaymentsSection
          operatorTransfer={operatorTransfer}
          commissionFee={operatorPricingView.commission}
          booking={booking}
          operator={operatorData}
        />
      )}
      <PricingsSection booking={booking} />
      <div className=" max-w-full w-full flex gap-y-6 flex-row-reverse">
        <Button
          onClick={() => {
            alert("Feature in development");
          }}
          variant="default"
        >
          Create an invoice
          <Plus size={16} className="black min-w-6 mx-1" />
        </Button>
      </div>
      <OperatorBrokerNotesSection booking={booking} tab="operator" />
    </React.Fragment>
  );
};

const OperatorSection = ({ operatorDetails, bookingId }: { operatorDetails: OperatorDetails; bookingId: string }) => {
  const [editOpen, setEditOpen] = useState(false);
  return (
    <Card className="w-full flex flex-col gap-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Operator details</h3>
        <Button onClick={() => setEditOpen(true)} variant="link">
          Edit
        </Button>
      </div>
      <EditOperatorDetailsModal
        bookingId={bookingId}
        open={editOpen}
        onOpenChange={setEditOpen}
        defaultValues={operatorDetails}
      />
      <div className="flex flex-row gap-4">
        <div className="w-[326px] flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span>Operator name</span>
            <span className="text-base font-medium	">{operatorDetails.name || "No data."}</span>
          </div>
          <div className="flex flex-col gap-2">
            <span>E-mail #2 (post-booking communication)</span>
            <span className="text-base font-medium	">{operatorDetails.email2 || "No data."}</span>
          </div>
          <div className="flex flex-col gap-2">
            <span>Address</span>
            <span className="text-base font-medium	">{operatorDetails.address || "No data."}</span>
          </div>
        </div>
        <div className="w-[326px] flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span>Contact person</span>
            <span className="text-base font-medium	">{operatorDetails.contactPerson || "No data."}</span>
          </div>
          <div className="flex flex-col gap-2">
            <span>Phone number</span>
            <span className="text-base font-medium	">{operatorDetails.phoneNumber || "No data."}</span>
          </div>
          <div className="flex flex-col gap-2">
            <span>Operator rating</span>
            <span className="text-base font-medium	">{operatorDetails.rating || "No data."}</span>
          </div>
        </div>
        <div className="w-[326px] flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span>E-mail</span>
            <span className="text-base font-medium	">{operatorDetails.email || "No data."}</span>
          </div>
          <div className="flex flex-col gap-2">
            <span>Operator's website</span>
            <span className="text-base font-medium	">{operatorDetails.website || "No data."}</span>
          </div>
        </div>{" "}
      </div>
    </Card>
  );
};

type UploadedFile = { id: string; name: string; url: string };

const ReferenceNumberSection = ({
  operator,
  handleCopyToClipboard,
  bookingId,
  initialInvoiceFile,
}: {
  operator: OperatorModel;
  handleCopyToClipboard: (text: string) => void;
  bookingId: string;
  initialInvoiceFile?: UploadedFile | null;
}) => {
  const [invoiceFile, setInvoiceFile] = useState<UploadedFile | null>(initialInvoiceFile ?? null);

  useEffect(() => {
    if (initialInvoiceFile) {
      setInvoiceFile(initialInvoiceFile);
    }
  }, [initialInvoiceFile]);

  return (
    <div className="flex w-full gap-6 items-center justify-between">
      <BookingCard className="flex w-[615px] items-center justify-between">
        <span className="text-base">Operator reference no.</span>
        <span
          onClick={(e) => {
            handleCopyToClipboard(e.currentTarget.innerText);
          }}
          className="flex items-center text-base text-base gap-2"
        >
          <span className="font-medium line-clamp-1 break-all">{operator.id || "N/A"}</span>
          <CopyIcon size={20} className="stroke-slate-500 min-w-6" />
        </span>
      </BookingCard>
      {invoiceFile ? (
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-lg">
            <Button className="justify-start" variant="link" asChild>
              <a
                href={invoiceFile.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 max-w-[300px] overflow-hidden"
                title={invoiceFile.name}
              >
                <span className="truncate flex-1">{invoiceFile.name}</span>
                <ExternalLink size={20} className="shrink-0" />
              </a>
            </Button>
          </div>

          <UploadInvoiceButton
            ownerType="operator_invoice"
            ownerId={bookingId}
            onUploaded={(file) => {
              setInvoiceFile(file as UploadedFile);
              console.log("Replaced invoice file:", file);
            }}
          >
            Replace invoice
          </UploadInvoiceButton>
        </div>
      ) : (
        <UploadInvoiceButton
          ownerType="operator_invoice"
          ownerId={bookingId}
          onUploaded={(file) => {
            setInvoiceFile(file as UploadedFile);
            console.log("Uploaded file:", file);
          }}
        >
          Upload Operator's invoice
        </UploadInvoiceButton>
      )}
    </div>
  );
};

const YachtSection = ({ booking, operator }: { booking: Booking; operator: OperatorModel }) => {
  return (
    <Card className="w-full flex flex-col gap-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Yacht details</h3>
      </div>
      <div className="grid grid-cols-3 gap-6">
        <div>
          <img
            className="min-w-[340px] h-[210px] rounded-[5px]"
            width={340}
            height={210}
            src={booking.offerYacht.yacht.media[0]}
            alt={booking.offerYacht.yacht.name}
          />
        </div>
        <div className="flex flex-col	gap-y-2 justify-center">
          <div className="flex flex-col gap-y-1">
            <span className="text-xl		">“{booking.offerYacht.yacht.name}”</span>
            <span className="text-base">
              {booking.offerYacht.yacht.modelName} | {booking.offerYacht.yacht.year}
            </span>
            <span className="text-sm	text-black">
              {booking.offerYacht.yacht.maxPeople} People • {booking.offerYacht.yacht.cabins} Cabins •{" "}
              {booking.offerYacht.yacht.bathrooms} Bathrooms
            </span>
          </div>
          <div className="flex flex-col gap-y-1">
            <span className="text-xs	text-slate-500">Operator</span>
            <span className="text-sm	text-black">
              {operator.name} | ({operator.rating}/10)
            </span>
          </div>
        </div>
        <div className="flex flex-col justify-end items-end">
          <div className="flex flex-row gap-x-4">
            <div className="flex flex-col">
              <span className="text-xs	text-slate-500">Enquiry date</span>
              <span>
                {booking.offer.enquiryDate ? format(parseISO(booking.offer.enquiryDate), "yyyy-MM-dd") : "N/A"}
              </span>
            </div>
            <Separator className="h-auto" orientation="vertical" />
            <div className="flex flex-col">
              <span className="text-xs	text-slate-500">Offer date</span>
              <span>
                {booking.offer.dateCreated ? format(parseISO(booking.offer.dateCreated), "yyyy-MM-dd") : "N/A"}
              </span>
            </div>
            <Separator className="h-auto" orientation="vertical" />
            <div className="flex flex-col">
              <span className="text-xs	text-slate-500">Booking date</span>
              <span>{booking.dateCreated ? format(parseISO(booking.dateCreated), "yyyy-MM-dd") : "N/A"}</span>
            </div>
          </div>
        </div>
      </div>
      {/* <Separator className="w-auto" orientation="horizontal" />
      <div className="flex flex-row">
        <div className="flex flex-row justify-start gap-x-6">
          <div className="grid grid-cols-4 gap-3 ">
            <div className="flex flex-col gap-y-1 w-[255px]">
              <span className="text-base text-black">Owner</span>
              <span className="text-base text-black font-medium	">TBD</span>
            </div>
            <div className="flex flex-col gap-y-1 w-[255px]">
              <span className="text-base text-black">Contact number</span>
              <span className="text-base text-black font-medium	">TBD</span>
            </div>
            <div className="flex flex-col gap-y-1 w-[255px]">
              <span className="text-base text-black">Address</span>
              <span className="text-base text-black font-medium	">TBD</span>
            </div>
            <div className="flex flex-col gap-y-1 w-[255px]">
              <span className="text-base text-black">VAT number</span>
              <span className="text-base text-black font-medium	">TBD</span>
            </div>
          </div>
        </div>
        <div className="flex">
          <Button
            onClick={() => {
              alert("Feature in development");
            }}
            variant="link"
          >
            Edit
          </Button>
        </div>
      </div> */}
    </Card>
  );
};

export const OperatorBrokerNotesSection = ({ booking, tab }: { booking: Booking; tab: "overview" | "operator" }) => {
  const [editBrokerNotesOpen, setEditBrokerNotesOpen] = useState(false);
  const currentNotes = !!booking?.operatorBrokerNotes ? booking.operatorBrokerNotes : "";
  return (
    <Card className="w-full flex flex-col gap-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Operator broker notes</h3>
        <Button onClick={() => setEditBrokerNotesOpen(true)} variant="link">
          Edit
        </Button>
      </div>
      <EditOperatorBrokerNotesModal
        tab={tab}
        open={editBrokerNotesOpen}
        onOpenChange={setEditBrokerNotesOpen}
        defaultValues={{
          notes: currentNotes,
        }}
        bookingId={booking?.id}
      />
      {!!booking.operatorBrokerNotes ? (
        <p>{booking.operatorBrokerNotes}</p>
      ) : (
        <span className="text--slate-700 text-base italic">No notes here</span>
      )}
    </Card>
  );
};

const PaymentsSection = ({
  booking,
  operator,
  commissionFee,
  operatorTransfer,
}: {
  booking: Booking;
  operator: OperatorModel;
  commissionFee: number;
  operatorTransfer: OperatorTransferDetails;
}) => {
  const [editTransferOpen, setEditTransferOpen] = useState(false);
  const bookingOperatorCurrencyCode = booking?.operatorTransfer?.currencyCode;
  const bookingYachtCurrencyCode = booking?.pricing?.yachtCurrencyCode;
  const operatorCurrencyCode =
    bookingOperatorCurrencyCode || bookingYachtCurrencyCode || operator?.transferDetails?.currencyCode || "EUR";

  const { data: paymentsData, isLoading: isLoadingPayments } = useQuery({
    queryKey: ["paymentsData", booking.id],
    queryFn: () => paymentsFetch<any[]>(`/api/payments/booking/${booking.id}`).then((res) => res),
    enabled: !!booking.id,
  });

  const hasOperatorPayment = (paymentsData ?? []).some(
    (payment) => payment.payerType === "Operator" && payment.removed === false && payment.status === "APPROVED",
  );

  return (
    <div className="flex w-full gap-6">
      <Card className="flex grow w-[688px] flex-col justify-between gap-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Bank transfer details</h3>
          <Button
            onClick={() => {
              setEditTransferOpen(true);
            }}
            variant="link"
          >
            Edit
          </Button>
        </div>
        <EditOperatorTransferModal
          open={editTransferOpen}
          onOpenChange={setEditTransferOpen}
          defaultValues={operatorTransfer}
          bookingId={booking.id}
          hasOperatorPayment={hasOperatorPayment}
        />
        <div className="flex flex-row justify-between">
          <div className="flex flex-col">
            <span>Recipient name</span>
            <span className="font-medium">{operatorTransfer?.recipientName || "No data."}</span>
          </div>
          <div className="flex flex-col">
            <span>BIC/SWIFT</span>
            <span className="font-medium">{operatorTransfer?.accountNumber || "No data"}</span>
          </div>
          <div className="flex flex-col">
            <span>IBAN</span>
            <span className="font-medium">{operatorTransfer?.iban || "No data."}</span>
          </div>
          <div className="flex flex-col">
            <span>Currency</span>
            <span className="font-medium">{operatorTransfer?.currencyCode || "No data."}</span>
          </div>
        </div>
      </Card>
      <Card className="flex grow w-[356px] flex-col justify-between gap-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Discounts & revenues</h3>
          {/* <Button
            onClick={() => {
              alert("Feature in development");
            }}
            variant="link"
          >
            Edit
          </Button> */}
        </div>
        <div className="flex flex-row justify-between">
          <div className="flex flex-col">
            <span>Commission fee</span>
            <span className="font-medium">{formatPrice(commissionFee, operatorCurrencyCode)}</span>
          </div>
          <div className="flex flex-col">
            <span>Custom discount</span>
            <span className="font-medium">{operator.customDiscount ? operator.customDiscount + "%" : "No data."}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};

const PricingsSection = ({ booking }: { booking: Booking }) => {
  const { operatorData } = useBookingData(booking);
  const operatorCurrencyCode = resolveOperatorCurrencyCode(booking, operatorData);

  return (
    <div className="flex w-full gap-6">
      <OperatorPricingCard
        booking={booking}
        operatorCurrencyCode={operatorCurrencyCode}
        tab="operator"
        className="w-[355px]"
      />
      <Card className="flex grow w-[737px] flex-col gap-6">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Payment schedules</h3>
          <Button
            onClick={() => {
              alert("Feature in development");
            }}
            variant="link"
          >
            Edit
          </Button>
        </div>
        <span className="text--slate-700 text-base italic">Feature in development</span>
      </Card>
    </div>
  );
};

const schema = z.object({
  name: z.string().min(3).optional(),
  email: z.string().email().optional(),
  email2: z.string().email().or(z.literal("")),
  contactPerson: z.string().optional(),
  obligatory: z.string().optional(),
  phoneNumber: z.string().optional(),
  address: z.string().optional(),
  rating: z.number().optional(),
  website: z.string().optional(),
});

type OperatorDetailsForm = z.infer<typeof schema>;

const EditOperatorDetailsModal = ({
  open,
  onOpenChange,
  defaultValues,
  bookingId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: OperatorDetails;
  bookingId: string;
}) => {
  const { updateOperatorDetails } = useBookingOperatorMutation(bookingId);
  const form = useForm<OperatorDetailsForm>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues,
  });

  const onSubmit = useCallback(
    form.handleSubmit(
      (data) => {
        updateOperatorDetails({
          operatorDetails: data,
        });
        onOpenChange(false);
      },
      () => {
        console.error("FORM ERROR", form.formState.errors, form.getValues());
      },
    ),
    [form],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-3xl flex flex-col">Edit operator details</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Operator name</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="contactPerson"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Contact person</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">E-mail</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="email2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">E-mail (post-booking)</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Phone number</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Operator's website</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Address</FormLabel>
                  <FormControl>
                    <Textarea className="resize-none" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="rating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Operator rating</FormLabel>
                  <FormControl>
                    <ScoreSelect min={1} max={10} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button onClick={onSubmit} type="submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const pricingSchema = z.object({
  clientPrice: z.number(),
  netCharterFee: z.number(),
  extras: z.number(),
});

type OperatorPricingForm = z.infer<typeof pricingSchema>;

export const EditOperatorPricingModal = ({
  open,
  onOpenChange,
  defaultValues,
  bookingId,
  tab,
  operatorCurrencyCode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: OperatorPricing;
  bookingId: string;
  tab: "overview" | "operator";
  operatorCurrencyCode: string;
}) => {
  const { updateOperatorPricing } = useBookingOperatorMutation(bookingId, tab);
  const form = useForm<OperatorPricingForm>({
    resolver: zodResolver(pricingSchema),
    defaultValues: defaultValues,
  });

  const onSubmit = useCallback(
    form.handleSubmit(
      (data) => {
        updateOperatorPricing({
          operatorPricing: data,
        });
        onOpenChange(false);
      },
      () => {
        console.error("FORM ERROR", form.formState.errors, form.getValues());
      },
    ),
    [form],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[720px] max-h-[calc(100%-80px)] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-3xl flex flex-col">Edit operator pricing</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              name="clientPrice"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Client price ({operatorCurrencyCode})</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} onChange={(e) => field.onChange(+e.target.value)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="netCharterFee"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Net charter fee ({operatorCurrencyCode})</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} onChange={(e) => field.onChange(+e.target.value)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="extras"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Extras ({operatorCurrencyCode})</FormLabel>
                  <FormControl>
                    <Input type="number" {...field} onChange={(e) => field.onChange(+e.target.value)} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button onClick={onSubmit} type="submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const notesSchema = z.object({
  notes: z.string().optional(),
});

type OperatorBrokerNotesForm = z.infer<typeof notesSchema>;
const EditOperatorBrokerNotesModal = ({
  open,
  onOpenChange,
  defaultValues,
  bookingId,
  tab,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: {
    notes?: string;
  };
  bookingId: string;
  tab: "overview" | "operator";
}) => {
  const { updateOperatorBrokerNotes } = useBookingOperatorMutation(bookingId, tab);
  const form = useForm<OperatorBrokerNotesForm>({
    resolver: zodResolver(notesSchema),
    defaultValues: defaultValues,
  });

  const onSubmit = useCallback(
    form.handleSubmit(
      (data) => {
        updateOperatorBrokerNotes({
          notes: data.notes,
        });
        onOpenChange(false);
      },
      () => {
        console.error("FORM ERROR", form.formState.errors, form.getValues());
      },
    ),
    [form],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[560px] max-h-[calc(100%-80px)] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-3xl flex flex-col">Edit broker notes</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <div className="grid">
            <FormField
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Broker notes</FormLabel>
                  <FormControl>
                    <Textarea className="w-full" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button onClick={onSubmit} type="submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const transferSchema = z.object({
  recipientName: z.string().optional(),
  currencyCode: z.string().optional(),
  accountNumber: z.string().optional(),
  iban: z.string().optional(),
});

type OperatorTransferForm = z.infer<typeof transferSchema>;
const EditOperatorTransferModal = ({
  open,
  onOpenChange,
  defaultValues,
  bookingId,
  hasOperatorPayment,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultValues: OperatorTransferDetails;
  bookingId: string;
  hasOperatorPayment: boolean;
}) => {
  const { updateOperatorTransfer } = useBookingOperatorMutation(bookingId);
  const form = useForm<OperatorTransferForm>({
    resolver: zodResolver(transferSchema),
    defaultValues: defaultValues,
  });

  const onSubmit = useCallback(
    form.handleSubmit(
      (data) => {
        updateOperatorTransfer(data);
        onOpenChange(false);
      },
      () => {
        console.error("FORM ERROR", form.formState.errors, form.getValues());
      },
    ),
    [form],
  );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-[560px] max-h-[calc(100%-80px)] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-3xl flex flex-col">Edit bank transfer details</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <div className="grid grid-cols-2 gap-4">
            <FormField
              name="recipientName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Recipient name</FormLabel>
                  <FormControl>
                    <Input className="w-full" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="accountNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">BIC/SWIFT</FormLabel>
                  <FormControl>
                    <Input className="w-full" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="iban"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">IBAN</FormLabel>
                  <FormControl>
                    <Input className="w-full" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              name="currencyCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-slate-600">Currency</FormLabel>
                  <FormControl>
                    <CurrencySelect {...field} disabled={hasOperatorPayment} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </Form>
        <DialogFooter>
          <Button onClick={onSubmit} type="submit">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

function formatPrice(price: number, code: string) {
  return new Intl.NumberFormat("en-EN", { style: "currency", currency: code }).format(price) || "N/A";
}
